import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../lib/mixpanel';
import { useAuthStore } from '../store/authstore';

/**
 * Hook to automatically track page views with Mixpanel
 *
 * Usage: Call this hook in your layout components (DashboardLayout, AuthLayout)
 * to automatically track all page navigations.
 *
 * Features:
 * - Tracks page name and path
 * - Tracks time spent on previous page
 * - Includes user role in page view events
 * - Tracks referrer page
 */
export function useMixpanelPageTracking() {
  const location = useLocation();
  const { session } = useAuthStore();
  const pageLoadTime = useRef<number>(Date.now());
  const previousPage = useRef<string | null>(null);

  useEffect(() => {
    const currentTime = Date.now();
    const timeOnPreviousPage = previousPage.current
      ? currentTime - pageLoadTime.current
      : undefined;

    // Get readable page name from path
    const pageName = getPageNameFromPath(location.pathname);

    // Track page view
    trackPageView(pageName, {
      user_role: session?.user?.roleType,
      section: getSection(location.pathname),
      time_on_previous_page: timeOnPreviousPage,
      referrer_page: previousPage.current,
    });

    // Update refs for next page view
    previousPage.current = location.pathname;
    pageLoadTime.current = currentTime;
  }, [location.pathname, session?.user?.roleType]);
}

/**
 * Convert URL path to readable page name
 */
function getPageNameFromPath(path: string): string {
  // Remove leading slash
  const cleanPath = path.replace(/^\//, '');

  // Handle special cases
  if (cleanPath === '' || cleanPath === 'dashboard') return 'Dashboard';
  if (cleanPath === 'login') return 'Login';
  if (cleanPath === 'signup') return 'Signup';

  // Convert path segments to title case
  const segments = cleanPath.split('/').filter(Boolean);
  return segments
    .map((segment) => {
      // Skip IDs and special segments
      if (segment === 'new' || segment === 'edit' || /^[0-9a-f-]{36}$/.test(segment)) {
        return null;
      }
      // Convert kebab-case to Title Case
      return segment
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    })
    .filter(Boolean)
    .join(' - ');
}

/**
 * Determine the section/category of the page
 */
function getSection(path: string): string {
  if (path.startsWith('/students')) return 'Students';
  if (path.startsWith('/employees')) return 'Employees';
  if (path.startsWith('/classes')) return 'Academic - Classes';
  if (path.startsWith('/subjects')) return 'Academic - Subjects';
  if (path.startsWith('/exams')) return 'Academic - Exams';
  if (path.startsWith('/attendance')) return 'Academic - Attendance';
  if (path.startsWith('/timetable')) return 'Schedule - Timetable';
  if (path.startsWith('/fees')) return 'Finance - Fees';
  if (path.startsWith('/salary')) return 'Finance - Salary';
  if (path.startsWith('/leaves')) return 'Leave Management';
  if (path.startsWith('/holidays')) return 'Calendar - Holidays';
  if (path.startsWith('/events')) return 'Calendar - Events';
  if (path.startsWith('/transport')) return 'Transport Management';
  if (path.startsWith('/hostel')) return 'Hostel Management';
  if (path.startsWith('/institute')) return 'Institute Settings';
  if (path.startsWith('/profile')) return 'User Profile';
  if (path.startsWith('/dashboard')) return 'Dashboard';
  if (path.startsWith('/reports')) return 'Reports';
  return 'Other';
}
